import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LockKey, EnvelopeSimple, Phone } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "team_owner" ? "/team" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      const to = res.user.role === "team_owner" ? "/team" : "/dashboard";
      nav(to, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" style={{ fontFamily: "Inter, sans-serif" }} data-testid="login-page">
      {/* LEFT — blue hero panel matching home page */}
      <div className="relative bg-blue-500 text-white overflow-hidden hidden lg:flex flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 30%, white 0%, transparent 30%), radial-gradient(circle at 80% 60%, white 0%, transparent 20%)" }} />
        <Link to="/" className="relative z-10 flex items-center gap-3">
          <img src="/nitisoft-logo.png" alt="NitiSoft" className="h-12 bg-white rounded p-1" />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest">NitiSoft</div>
            <div className="text-[10px] uppercase tracking-widest opacity-80">Player Auction</div>
          </div>
        </Link>

        <div className="relative z-10">
          <div className="text-xs uppercase tracking-widest font-bold text-yellow-300 mb-3">WELCOME BACK</div>
          <h1 className="font-black uppercase leading-[1.05]" style={{ fontSize: "clamp(40px, 5vw, 68px)" }}>
            Run your next<br />player auction.
          </h1>
          <p className="mt-4 text-white/95 max-w-md text-lg">
            Sign in to manage tournaments, run live bidding, control LED &amp; stream overlays,
            and hand every team owner a private bidding room — all from one dashboard.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            {[
              ["Live", "Bidding"],
              ["Broadcast", "Overlays"],
              ["Reports", "& Audit"],
            ].map(([l1, l2]) => (
              <div key={l1} className="bg-white/10 backdrop-blur rounded-lg px-3 py-3 text-center border border-white/20">
                <div className="font-black text-lg">{l1}</div>
                <div className="text-xs uppercase tracking-widest opacity-80">{l2}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-white/70 flex items-center gap-2">
          <Phone size={12} weight="fill" /> +91-9970228557 · nitisoft.in
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-6" data-testid="login-brand-mobile">
            <img src="/nitisoft-logo.png" alt="NitiSoft" className="h-10" />
            <span className="font-bold text-blue-600 uppercase tracking-widest text-sm">NitiSoft Auction</span>
          </Link>
          <div className="text-xs font-bold uppercase tracking-widest text-red-500 mb-2">SIGN IN</div>
          <h2 className="font-black text-4xl sm:text-5xl mb-2 uppercase leading-tight text-gray-900">Welcome back to the floor.</h2>
          <p className="text-gray-600 mb-8">Manage your tournaments, teams and live auctions in one place.</p>

          <form onSubmit={submit} data-testid="login-form" className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-600 block mb-2">Email</label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input data-testid="login-email-input" type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       className="w-full bg-gray-50 border-2 border-gray-200 focus:border-blue-500 focus:bg-white outline-none rounded-lg pl-10 pr-4 py-3 text-gray-900 placeholder:text-gray-400"
                       placeholder="you@nitisoft.in" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-gray-600 block mb-2">Password</label>
              <div className="relative">
                <LockKey className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input data-testid="login-password-input" type="password" required value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       className="w-full bg-gray-50 border-2 border-gray-200 focus:border-blue-500 focus:bg-white outline-none rounded-lg pl-10 pr-4 py-3 text-gray-900 placeholder:text-gray-400"
                       placeholder="••••••••" />
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-red-700 text-sm border-2 border-red-200 bg-red-50 rounded-lg px-3 py-2 font-semibold">{error}</div>}

            <button type="submit" disabled={busy} data-testid="login-submit-button"
                    className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold uppercase tracking-widest px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              {busy ? "Signing in…" : "Sign In"}
            </button>

            <div className="text-sm text-gray-600 pt-2 flex justify-between font-semibold">
              <Link to="/" className="hover:text-blue-600">← Home</Link>
              <Link to="/register" className="hover:text-blue-600" data-testid="link-to-register">Create an organizer account →</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
